import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ShieldOff, X, ExternalLink } from 'lucide-react';
import type { BlackoutWindow } from '../docks/BlackoutList';

// ─────────────────────────────────────────────────────
//  BlackoutDetailPopover — View-only popover for
//  blackout blocks on timelines. No delete action.
//  V1.3C: Click a blackout block → see details.
// ─────────────────────────────────────────────────────

interface BlackoutDetailPopoverProps {
    blackout: BlackoutWindow;
    anchorRect: DOMRect;
    onClose: () => void;
}

const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export function BlackoutDetailPopover({ blackout, anchorRect, onClose }: BlackoutDetailPopoverProps) {
    const popoverRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    // Position popover near the anchor element
    useEffect(() => {
        const el = popoverRef.current;
        if (!el) return;
        const elRect = el.getBoundingClientRect();

        // Default: below the anchor, horizontally centered
        let top = anchorRect.bottom + 8;
        let left = anchorRect.left + anchorRect.width / 2 - elRect.width / 2;

        // Clamp to viewport
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        if (left < 8) left = 8;
        if (left + elRect.width > vw - 8) left = vw - elRect.width - 8;
        if (top + elRect.height > vh - 8) {
            top = anchorRect.top - elRect.height - 8; // flip above
        }

        setPos({ top, left });
    }, [anchorRect]);

    // Close on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    // Close on Escape
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const isAllDocks = blackout.isWarehouseWide;
    const startDate = fmtDate(blackout.startAt);
    const endDate = fmtDate(blackout.endAt);
    const sameDay = startDate === endDate;

    return createPortal(
        <div
            ref={popoverRef}
            className="fixed z-[300] w-72 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl animate-in fade-in-0 zoom-in-95"
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-3 pb-2">
                <div className="flex items-center gap-2">
                    <ShieldOff className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-bold text-slate-900 dark:text-slate-100">Blocked Window</span>
                </div>
                <button
                    onClick={onClose}
                    className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>

            {/* Body */}
            <div className="px-4 pb-3 space-y-2">
                {/* Scope badge */}
                <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                        isAllDocks
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                    }`}>
                        {isAllDocks ? 'All Docks' : `Dock ${blackout.dockNumber}`}
                    </span>
                </div>

                {/* Reason */}
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {blackout.reason}
                </div>

                {/* Time window */}
                <div className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                    {sameDay ? (
                        <>{startDate} · {fmtTime(blackout.startAt)} – {fmtTime(blackout.endAt)}</>
                    ) : (
                        <>{startDate} {fmtTime(blackout.startAt)} – {endDate} {fmtTime(blackout.endAt)}</>
                    )}
                </div>

                {/* Created by */}
                {blackout.createdByName && (
                    <div className="text-[11px] text-slate-400 dark:text-slate-500">
                        Created by {blackout.createdByName}
                    </div>
                )}
            </div>

            {/* Footer — Manage link */}
            <div className="px-4 py-2.5 border-t border-slate-100 dark:border-slate-800">
                <a
                    href="/dock-management"
                    className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                >
                    <ExternalLink className="w-3 h-3" />
                    Manage in Dock Operations
                </a>
            </div>
        </div>,
        document.body
    );
}
